<template>
    <sidebar-menu
        :items="sidebarMenuItems"
        has-icon
        :force-expanded="forceExpanded"
        @update:expanded="$emit('update:expanded', $event)"
    />
</template>

<script>
import { mapState } from 'vuex';

import SidebarMenu from '@/components/sidebar/SidebarMenu.vue';

export default {
    name: 'UserSideBar',

    components: { SidebarMenu },

    emits: ['update:expanded'],

    props: {
        forceExpanded: {
            type: Boolean,
            required: false,
            default: false,
        },
    },

    computed: {
        ...mapState('Auth', { user: state => state.user }),

        userPermission() {
            return (
                this.user.hasPermissionTo('view removed course enrollments') ||
                this.user.hasGroupPermissionTo('view removed course enrollments')
            );
        },

        canViewRemediation() {
            return (
                this.user.hasPermissionTo('view all remediation processes') ||
                this.user.hasGroupPermissionTo('view all remediation processes')
            );
        },

        canViewGrades() {
            return (
                this.user.hasPermissionTo('view all course grade') ||
                this.user.hasGroupPermissionTo('view all course grade')
            );
        },

        canViewOtherUserEvaluationDashboard() {
            return this.user.hasPermissionTo("view another user's evaluation dashboard");
        },

        isViewingOwnProfile() {
            return this.$route.matched[0].path === '/me';
        },

        canViewOwnEbooks() {
            return this.$gate.allows('viewAny', 'Ebook') || this.user.hasRole('Student');
        },

        canViewOtherUserEbooks() {
            return this.$gate.allows('viewAny', 'Ebook');
        },

        showInstitutionTranscriptLink() {
            return !!(
                this.$store.getters['InstitutionSettings/get']('transcript_link') &&
                this.user.hasRole('Student') &&
                !this.user.is_faculty
            );
        },

        sidebarMenuItems() {
            let sidebarItems = [];
            const isCurrentUserRoute = this.$route.matched[0].path === '/me';

            this.addProfileMenuItem(sidebarItems, isCurrentUserRoute);

            if (isCurrentUserRoute) {
                this.addCurrentUserSpecificMenuItems(sidebarItems);
            } else {
                this.addOtherUserSpecificMenuItems(sidebarItems);
            }

            sidebarItems = sidebarItems.filter(item => item !== null);
            return sidebarItems;
        },
    },

    methods: {
        addProfileMenuItem(sidebarItems, isCurrentUserRoute) {
            const profileRoute = isCurrentUserRoute
                ? { name: 'me' }
                : { name: 'person-view', params: { personId: this.$route.params.personId } };

            const profileMenuItem = this.createSidebarMenuItem(
                'Profile',
                'user',
                profileRoute,
                this.$gate.allows('view', ['Status'])
            );

            sidebarItems.push(profileMenuItem);
        },

        addCurrentUserSpecificMenuItems(sidebarItems) {
            const currentUserMenuItems = [
                this.createSidebarMenuItem('Enrollments', 'pi pi-graduation-cap', { name: 'my-enrollments' }),
                this.createSidebarMenuItem('Surveys', 'pi pi-pencil', { name: 'my-surveys' }),
                this.canViewOwnEbooks ? this.createSidebarMenuItem('Ebooks', 'book', { name: 'my-ebook' }) : null,
                this.createSidebarMenuItem('Grades', 'pi pi-chart-bar', { name: 'my-grades' }),

                this.$hasFeature('INBOX')
                    ? this.createSidebarMenuItem('Notifications', 'pi pi-globe', { name: 'my-notifications' })
                    : null,

                this.userPermission ? this.createSidebarMenuItem('Access', 'pi pi-lock', { name: 'my-access' }) : null,

                this.showInstitutionTranscriptLink
                    ? this.createSidebarMenuItem('Transcript', 'pi pi-book', { name: 'my-transcript' })
                    : null,

                this.createSidebarMenuItem('Evaluations', 'pi pi-file', {
                    name: 'evaluation-instructor',
                    params: { userId: this.isViewingOwnProfile ? this.user.id : this.$route.params.personId },
                }),
            ];

            sidebarItems.push(...currentUserMenuItems.filter(item => item !== null));
        },

        addOtherUserSpecificMenuItems(sidebarItems) {
            const otherUserMenuItems = [
                this.createSidebarMenuItem(
                    'Enrollments',
                    'graduation-cap',
                    { name: 'person-enrollments' },
                    this.userPermission
                ),
                this.canViewGrades
                    ? this.createSidebarMenuItem('Grades', 'pi pi-chart-line', { name: 'person-grades' })
                    : null,

                this.$hasFeature('INBOX')
                    ? this.createSidebarMenuItem('Notifications', 'globe', {
                          name: 'person-notifications',
                          params: { personId: this.$route.params.personId },
                      })
                    : null,

                this.canViewOtherUserEbooks
                    ? this.createSidebarMenuItem('Ebooks', 'book', { name: 'person-ebook' })
                    : null,
                this.createSidebarMenuItem('Access', 'lock', { name: 'person-access' }),
                this.createSidebarMenuItem('Risk Assessment', 'pi pi-chart-scatter', {
                    name: 'person-risk-assessment',
                }),

                this.canViewRemediation
                    ? this.createSidebarMenuItem('Remediation', 'pi pi-file-check', { name: 'person-remediations' })
                    : null,

                this.createSidebarMenuItem('Evaluations', 'pi pi-file', {
                    name: 'evaluation-instructor',
                    params: { userId: this.isViewingOwnProfile ? this.user.id : this.$route.params.personId },
                }),
            ];

            sidebarItems.push(...otherUserMenuItems.filter(item => item !== null));
        },

        createSidebarMenuItem(title, icon, route, condition = true) {
            return condition ? { type: 'item', title, icon, route } : null;
        },
    },
};
</script>
